(function () {
  const ACTIVE_CLASS = 'is-active';

  class ProductComparisonModal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.modal = document.getElementById(this.dataset.modalId || '');
      if (!this.modal) return;

      this.currency = this.dataset.currency || 'USD';
      this.currentProduct = this.readJson('[data-current-product]');
      this.products = this.readJson('[data-comparison-products]', []);
      this.primaryProduct = this.getPrimaryProduct();
      this.currentVariant = this.getCurrentVariantFromDom() || this.getVariantById(this.currentProduct.currentVariantId);

      this.filterLayer = this.modal.querySelector('[data-comparison-layer="filters"]');
      this.resultsLayer = this.modal.querySelector('[data-comparison-layer="results"]');
      this.resultsContainer = this.modal.querySelector('[data-comparison-results]');
      this.startButton = this.modal.querySelector('[data-comparison-start]');
      this.backButton = this.modal.querySelector('[data-comparison-back]');
      this.filterButtons = Array.from(this.modal.querySelectorAll('[data-filter-button]'));
      this.pickMedia = this.modal.querySelector('[data-current-pick-media]');
      this.pickName = this.modal.querySelector('[data-current-pick-name]');
      this.filterRows = this.getFilterRows();

      this.selection = {
        series: this.modal.querySelector('[data-group="series"]')?.dataset.value || '',
        material: null,
        height: null,
        feel: null,
        sleepSurface: null,
      };

      this.applyDefaultSelections();
      this.bindEvents();
      this.syncCurrentProductState();
      this.syncFeelSelectionFromCurrentVariant();
      this.updateCurrentPickDisplay();
      this.toggleEmptyState();
      this.showFilters();
    }

    bindEvents() {
      this.filterButtons.forEach((button) => {
        if (button.disabled) return;
        button.addEventListener('click', () => this.onFilterButtonClick(button));
      });

      this.startButton?.addEventListener('click', () => {
        if (!this.validateSelections()) return;

        this.logSubmittedFilters();
        this.showResults();
        this.resultsContainer.innerHTML =
          '<div class="product-comparison-modal__state">Finding matching mattresses...</div>';

        requestAnimationFrame(() => {
          const matches = this.getMatches();
          console.info('[Product comparison] Matched products:', matches);
          this.renderResults(matches);
        });
      });

      this.backButton?.addEventListener('click', () => this.showFilters());

      this.getOpenTriggers().forEach((trigger) => {
        trigger.addEventListener('click', () => {
          this.currentVariant = this.getCurrentVariantFromDom() || this.currentVariant;
          this.syncCurrentProductState();
          this.syncFeelSelectionFromCurrentVariant();
          this.updateCurrentPickDisplay();
        });
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = this.normalizeRuntimeVariant(event?.data?.variant);
          if (!variant || !this.getVariantById(variant.id)) return;

          this.currentVariant = variant;
          this.syncCurrentProductState();
          this.syncFeelSelectionFromCurrentVariant();
          this.updateCurrentPickDisplay();
        });
      }

      document.body.addEventListener('modalClosed', () => {
        if (!this.modal.hasAttribute('open')) {
          this.showFilters();
        }
      });
    }

    readJson(selector, fallback = {}) {
      const node = this.modal.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[Product comparison] Failed to parse JSON.', error);
        return fallback;
      }
    }

    applyDefaultSelections() {
      this.setDefaultSelection('material', normalizeMaterial(this.currentProduct.material));
      this.setDefaultSelection('height', normalizeHeight(this.currentProduct.height));
      this.setDefaultSelection('feel', normalizeText(this.currentProduct.feel));
      this.setDefaultSelection('sleepSurface', normalizeText(this.currentProduct.sleepSurface));
    }

    setDefaultSelection(group, normalizedValue) {
      if (!normalizedValue) return;

      const button = this.filterButtons.find((item) => {
        if (item.dataset.group !== group) return false;
        return normalizeText(item.dataset.value) === normalizedValue;
      });

      if (!button) return;
      this.activateButton(button, false);
    }

    onFilterButtonClick(button) {
      const group = button.dataset.group;
      const isActive = button.classList.contains(ACTIVE_CLASS);

      this.filterButtons.forEach((item) => {
        if (item.dataset.group === group) {
          item.classList.remove(ACTIVE_CLASS);
        }
      });

      if (isActive) {
        this.selection[group] = null;
        return;
      }

      this.activateButton(button, true);
    }

    activateButton(button, shouldStore) {
      button.classList.add(ACTIVE_CLASS);

      if (shouldStore) {
        this.selection[button.dataset.group] = button.dataset.value;
        return;
      }

      this.selection[button.dataset.group] = button.dataset.value;
    }

    toggleEmptyState() {
      const comparableProducts = this.products.filter(
        (product) => String(product.id) !== String(this.currentProduct.id)
      );

      if (comparableProducts.length > 0) return;

      this.startButton?.setAttribute('disabled', 'disabled');
      this.startButton?.classList.add('is-disabled');
      this.startButton?.insertAdjacentHTML(
        'afterend',
        '<p class="product-comparison-modal__helper">No other products were found in this series yet.</p>'
      );
    }

    showFilters() {
      this.filterLayer.hidden = false;
      this.resultsLayer.hidden = true;
    }

    showResults() {
      this.filterLayer.hidden = true;
      this.resultsLayer.hidden = false;
    }

    getMatches() {
      const products = this.products.filter((product) => String(product.id) !== String(this.currentProduct.id));

      return products.filter((product) => {
        if (this.selection.material) {
          const productMaterial = normalizeMaterial(product.material || product.coreMaterials);
          if (productMaterial !== normalizeMaterial(this.selection.material)) return false;
        }

        if (this.selection.height) {
          const heights = splitValues(product.height).map(normalizeHeight).filter(Boolean);
          if (!heights.includes(normalizeHeight(this.selection.height))) return false;
        }

        if (this.selection.sleepSurface) {
          if (normalizeText(product.sleepSurface) !== normalizeText(this.selection.sleepSurface)) return false;
        }

        if (this.selection.feel) {
          const feelValues = getVariantFeelValues(product);
          if (!feelValues.includes(normalizeText(this.selection.feel))) return false;
        }

        return true;
      });
    }

    renderResults(products) {
      if (!products.length) {
        this.resultsContainer.innerHTML = `
          <div class="product-comparison-modal__state">
            <p>No matching mattresses found.</p>
            <p>Try adjusting your filters.</p>
          </div>
        `;
        return;
      }

      const comparisonProducts = [this.primaryProduct, ...products].filter(Boolean);
      const cardsHtml = comparisonProducts.map((product, index) => this.renderCard(product, index === 0)).join('');
      const tableHtml = this.renderTable(comparisonProducts);

      this.resultsContainer.innerHTML = `
        <div class="product-comparison-modal__cards">${cardsHtml}</div>
        <div class="product-comparison-modal__table-wrap">${tableHtml}</div>
      `;
    }

    renderCard(product, isPrimary = false) {
      const preferredVariant = getPreferredVariant(
        product,
        this.selection.feel,
        isPrimary ? this.currentProduct.currentVariantId : null
      );
      const priceText = formatMoney(preferredVariant?.price ?? 0, this.currency);
      const comparePriceValue = Number(preferredVariant?.compareAtPrice || 0);
      const comparePriceText =
        comparePriceValue > Number(preferredVariant?.price || 0) ? formatMoney(comparePriceValue, this.currency) : '';
      const imageUrl = preferredVariant?.image || product.image || '';
      const imageAlt = escapeHtml(product.imageAlt || product.title || '');
      const productUrl = getVariantUrl(product.url || '#', preferredVariant?.id);

      return `
        <article class="product-comparison-modal__card">
          <a class="product-comparison-modal__card-link" href="${escapeHtml(productUrl)}">
            <div class="product-comparison-modal__card-media">
              ${imageUrl
                ? `<img src="${escapeHtml(imageUrl)}" alt="${imageAlt}" loading="lazy">`
                : '<div class="product-comparison-modal__card-placeholder"></div>'}
            </div>
            <div class="product-comparison-modal__card-body">
              ${isPrimary ? '<p class="product-comparison-modal__card-badge">Your Pick</p>' : ''}
              <h3 class="product-comparison-modal__card-title">${escapeHtml(product.title || '')}</h3>
              <p class="product-comparison-modal__card-price">
                <span>${priceText}</span>
                ${comparePriceText ? `<s>${comparePriceText}</s>` : ''}
              </p>
              <span class="product-comparison-modal__card-cta">Shop Now</span>
            </div>
          </a>
        </article>
      `;
    }

    renderTable(products) {
      const rows = [
        {
          label: 'Height',
          render: (product) => renderHeightValue(firstValue(product.height) || '-'),
          isHtml: true,
        },
        {
          label: 'Construction',
          render: (product) => escapeHtml(getConstructionLabel(product)),
        },
        {
          label: 'Sleep Feel',
          render: (product, preferredVariantId, index) =>
            escapeHtml(
              index === 0
                ? getDisplayFeel(product, null, preferredVariantId)
                : this.selection.feel || getDisplayFeel(product, this.selection.feel, preferredVariantId)
            ),
        },
        {
          label: 'Cooling',
          render: (product) => renderSnowflakes(getCoolingScore(product.cooling)),
          isHtml: true,
        },
        {
          label: 'Support',
          render: (product) => renderSegments(getNumeric(product.support)),
          isHtml: true,
        },
        {
          label: 'Pressure Relief',
          render: (product) => renderSegments(getNumeric(product.pressureRelief)),
          isHtml: true,
        },
        {
          label: 'Cover',
          render: (product) => escapeHtml(getCoverLabel(this.selection.sleepSurface || product.sleepSurface, product.seriesKey)),
        },
        {
          label: 'Best For',
          render: (product, preferredVariantId, index) =>
            escapeHtml(
              getBestForLabel(
                index === 0
                  ? getDisplayFeel(product, null, preferredVariantId)
                  : this.selection.feel || getDisplayFeel(product, this.selection.feel, preferredVariantId),
                this.selection.sleepSurface || product.sleepSurface,
                product.seriesKey
              )
            ),
        },
        {
          label: 'Warranty',
          render: () => '10-Year Limited Warranty',
        },
      ];

      const bodyHtml = rows
        .map((row) => {
          const values = products
            .map((product, index) => {
              const preferredVariantId = index === 0 ? this.currentProduct.currentVariantId : null;
              return `<td>${
                row.isHtml
                  ? row.render(product, preferredVariantId, index)
                  : row.render(product, preferredVariantId, index)
              }</td>`;
            })
            .join('');
          return `<tr><th scope="row">${row.label}</th>${values}</tr>`;
        })
        .join('');

      return `
        <table class="product-comparison-modal__table">
          <tbody>${bodyHtml}</tbody>
        </table>
      `;
    }

    getPrimaryProduct() {
      const matchedProduct = this.products.find(
        (product) => String(product.id) === String(this.currentProduct.id)
      );

      if (!matchedProduct) return this.currentProduct;

      return {
        ...matchedProduct,
        ...this.currentProduct,
      };
    }

    getOpenTriggers() {
      return Array.from(document.querySelectorAll(`[data-modal="#${this.modal.id}"] button`));
    }

    getFilterRows() {
      const rows = {};
      this.filterButtons.forEach((button) => {
        const group = button.dataset.group;
        if (!group || rows[group]) return;

        const row = button.closest('.product-comparison-modal__row');
        if (row) rows[group] = row;
      });
      return rows;
    }

    validateSelections() {
      const requiredGroups = ['material', 'height', 'feel', 'sleepSurface'];
      const missingGroups = requiredGroups.filter((group) => !this.selection[group]);

      Object.values(this.filterRows).forEach((row) => row.classList.remove('is-invalid'));

      if (!missingGroups.length) return true;

      missingGroups.forEach((group, index) => {
        const row = this.filterRows[group];
        if (!row) return;

        window.setTimeout(() => {
          row.classList.remove('is-invalid');
          void row.offsetWidth;
          row.classList.add('is-invalid');
        }, index * 80);
      });

      return false;
    }

    logSubmittedFilters() {
      console.group('[Product comparison] Start Comparison submitted');
      console.info('Current product:', {
        id: this.currentProduct.id,
        title: this.currentProduct.title,
        handle: this.currentProduct.handle,
        currentVariantId: this.currentProduct.currentVariantId,
      });
      console.info('Selected filters:', {
        series: this.selection.series || null,
        material: this.selection.material || null,
        height: this.selection.height || null,
        feel: this.selection.feel || null,
        sleepSurface: this.selection.sleepSurface || null,
      });
      console.info('Available comparison products:', this.products);
      console.groupEnd();
    }

    getCurrentVariantInput() {
      return (
        document.querySelector('#MainProduct-main input[name="id"]') ||
        document.querySelector('product-info input[name="id"]') ||
        document.querySelector('product-form input[name="id"]')
      );
    }

    getCurrentVariantFromDom() {
      const selectedOptionsVariant = this.getVariantBySelectedOptions(this.getSelectedOptionValues());
      if (selectedOptionsVariant) return selectedOptionsVariant;

      const input = this.getCurrentVariantInput();
      return this.getVariantById(input?.value);
    }

    getVariantById(variantId) {
      if (!variantId) return null;
      return (this.currentProduct.variants || []).find((variant) => String(variant.id) === String(variantId)) || null;
    }

    getVariantBySelectedOptions(selectedOptions) {
      if (!Array.isArray(selectedOptions) || !selectedOptions.length) return null;

      return (
        (this.currentProduct.variants || []).find((variant) => {
          if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;
          return variant.options.every((optionValue, index) => String(optionValue) === String(selectedOptions[index]));
        }) || null
      );
    }

    getSelectedOptionValues() {
      const variantSelects = document.querySelector('variant-selects');
      if (!variantSelects) return [];

      return Array.from(variantSelects.children)
        .map((group) => {
          const select = group.querySelector('select[name^="options["]');
          if (select) return select.value;

          const checkedInput = group.querySelector('input[type="radio"]:checked');
          if (checkedInput) return checkedInput.value;

          return null;
        })
        .filter(Boolean);
    }

    normalizeRuntimeVariant(variant) {
      if (!variant?.id) return null;

      const staticVariant = this.getVariantById(variant.id);
      return {
        ...(staticVariant || {}),
        id: variant.id,
        title: variant.title || staticVariant?.title || '',
        available: typeof variant.available === 'boolean' ? variant.available : staticVariant?.available,
        price: variant.price ?? staticVariant?.price ?? 0,
        compareAtPrice: variant.compare_at_price ?? variant.compareAtPrice ?? staticVariant?.compareAtPrice ?? 0,
        options: variant.options || staticVariant?.options || [],
        image:
          variant.featured_image?.src ||
          variant.featured_image?.url ||
          variant.featuredImage?.src ||
          variant.featuredImage?.url ||
          staticVariant?.image ||
          '',
      };
    }

    syncCurrentProductState() {
      const domVariant = this.getCurrentVariantFromDom();
      if (domVariant) {
        this.currentVariant = {
          ...domVariant,
          ...this.currentVariant,
        };
      }

      const activeVariant = this.currentVariant || this.getVariantById(this.currentProduct.currentVariantId);
      if (!activeVariant) return;

      this.currentProduct.currentVariantId = activeVariant.id;
      this.currentProduct.feel = getVariantFeelValue(this.currentProduct, activeVariant) || this.currentProduct.feel;
      this.primaryProduct = this.getPrimaryProduct();
      this.primaryProduct.currentVariantId = activeVariant.id;
      this.primaryProduct.feel = this.currentProduct.feel;
    }

    syncFeelSelectionFromCurrentVariant() {
      const activeVariant = this.currentVariant || this.getVariantById(this.currentProduct.currentVariantId);
      const feelValue = getVariantFeelValue(this.currentProduct, activeVariant) || this.currentProduct.feel;

      this.filterButtons.forEach((button) => {
        if (button.dataset.group === 'feel') {
          button.classList.remove(ACTIVE_CLASS);
        }
      });

      this.selection.feel = null;
      this.setDefaultSelection('feel', normalizeText(feelValue));
    }

    updateCurrentPickDisplay() {
      const activeVariant = this.currentVariant || this.getVariantById(this.currentProduct.currentVariantId);
      if (!activeVariant) return;

      if (this.pickMedia) {
        const imageUrl = activeVariant.image || this.currentProduct.image || '';
        const imageAlt = escapeHtml(this.currentProduct.imageAlt || this.currentProduct.title || '');
        if (imageUrl) {
          this.pickMedia.innerHTML = `<img src="${escapeHtml(imageUrl)}" alt="${imageAlt}" class="product-comparison-modal__pick-image" loading="lazy">`;
        }
      }

      if (this.pickName) {
        this.pickName.textContent = this.getCurrentPickName(activeVariant);
      }
    }

    getCurrentPickName(variant) {
      const title = this.currentProduct.title || '';
      const height = firstValue(this.currentProduct.height) || '';
      const feel = getVariantFeelValue(this.currentProduct, variant) || this.currentProduct.feel || '';
      return [title, height, feel].filter(Boolean).join(' - ');
    }
  }

  if (!customElements.get('product-comparison-modal')) {
    customElements.define('product-comparison-modal', ProductComparisonModal);
  }

  function getPreferredVariant(product, selectedFeel, preferredVariantId = null) {
    const options = Array.isArray(product.options) ? product.options : [];
    const feelIndex = getOptionIndex(options, ['feel', 'feeling', 'comfort level', 'comfort-level']);
    const sizeIndex = getOptionIndex(options, ['size']);
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const normalizedFeel = normalizeText(selectedFeel);

    let candidates = variants.slice();

    if (feelIndex > -1 && normalizedFeel) {
      const feelMatches = candidates.filter((variant) => {
        const value = normalizeText(variant.options?.[feelIndex]);
        return value === normalizedFeel;
      });

      if (feelMatches.length) candidates = feelMatches;
    }

    if (preferredVariantId) {
      const preferredVariant = candidates.find((variant) => String(variant.id) === String(preferredVariantId));
      if (preferredVariant) return preferredVariant;
    }

    if (sizeIndex > -1) {
      const queenVariant = candidates.find((variant) => normalizeText(variant.options?.[sizeIndex]) === 'queen');
      if (queenVariant) return queenVariant;
    }

    return candidates.find((variant) => variant.available) || candidates[0] || null;
  }

  function getVariantFeelValues(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const options = Array.isArray(product.options) ? product.options : [];
    const feelIndex = getOptionIndex(options, ['feel', 'feeling', 'comfort level', 'comfort-level']);

    if (feelIndex === -1) return [];

    return Array.from(
      new Set(
        variants
          .map((variant) => normalizeText(variant.options?.[feelIndex]))
          .filter(Boolean)
      )
    );
  }

  function getDisplayFeel(product, selectedFeel, preferredVariantId = null) {
    const variant = getPreferredVariant(product, selectedFeel, preferredVariantId);
    const options = Array.isArray(product.options) ? product.options : [];
    const feelIndex = getOptionIndex(options, ['feel', 'feeling', 'comfort level', 'comfort-level']);

    if (variant && feelIndex > -1) {
      return variant.options?.[feelIndex] || '-';
    }

    return firstValue(product.firmness) || '-';
  }

  function getVariantFeelValue(product, variant) {
    const options = Array.isArray(product.options) ? product.options : [];
    const feelIndex = getOptionIndex(options, ['feel', 'feeling', 'comfort level', 'comfort-level']);
    if (feelIndex === -1) return '';
    return variant?.options?.[feelIndex] || '';
  }

  function getConstructionLabel(product) {
    const material = normalizeMaterial(product.material);
    if (material === 'hybrid') return 'Hybrid';
    if (material === 'memory foam') return 'Memory Foam';
    return firstValue(product.material) || '-';
  }

  function getOptionIndex(optionNames, aliases) {
    const normalizedAliases = aliases.map(normalizeText);
    return optionNames.findIndex((optionName) => normalizedAliases.includes(normalizeText(optionName)));
  }

  function getCoolingScore(value) {
    const numeric = getNumeric(value);
    if (numeric > 0) return numeric;
    return normalizeCoolingLabel(value) === 'extra' ? 8 : 5;
  }

  function getCoverLabel(sleepSurface, seriesKey) {
    const normalizedSeries = normalizeText(seriesKey);
    const normalizedSurface = normalizeText(sleepSurface);

    const coverMap = {
      black: {
        cooling: 'Cooling Knit Cover',
        'extra cooling': 'Graphene-Enhanced Cooling Knit Cover',
      },
      tenzura: {
        'silky soft': 'Silky Soft Cover',
        'plush & silky': 'Plush & Silky Cover',
      },
    };

    const seriesMap = normalizedSeries.includes('tenzura') ? coverMap.tenzura : coverMap.black;
    return seriesMap[normalizedSurface] || firstValue(sleepSurface) || '-';
  }

  function getBestForLabel(feel, sleepSurface, seriesKey) {
    const normalizedFeel = normalizeText(feel);
    const normalizedSeries = normalizeText(seriesKey);

    const genericFeelMap = {
      firm: 'Back, Stomach & Combination Sleepers',
      'medium firm': 'Back & Combination Sleepers',
      'medium plush': 'Side, Back & Combination Sleepers',
      plush: 'Side, Back & Combination Sleepers',
    };

    const seriesSpecificMap = {
      black: {
        firm: 'Back, Stomach & Combination Sleepers',
        'medium firm': 'Back & Combination Sleepers',
        'medium plush': 'Side, Back & Combination Sleepers',
      },
      tenzura: {
        firm: 'Back, Stomach & Combination Sleepers',
        'medium firm': 'Back & Combination Sleepers',
        plush: 'Side, Back & Combination Sleepers',
      },
    };

    const seriesMap = normalizedSeries.includes('tenzura') ? seriesSpecificMap.tenzura : seriesSpecificMap.black;
    return seriesMap[normalizedFeel] || genericFeelMap[normalizedFeel] || firstValue(sleepSurface) || '-';
  }

  function getNumeric(value) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return Math.max(0, Math.min(10, parsed));
    return 0;
  }

  function splitValues(value) {
    if (Array.isArray(value)) {
      return value.flatMap((item) => splitValues(item));
    }

    if (value === null || value === undefined) return [];

    return String(value)
      .split(/[|,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function firstValue(value) {
    return splitValues(value)[0] || (typeof value === 'string' ? value.trim() : '');
  }

  function normalizeMaterial(value) {
    const normalized = normalizeText(firstValue(value));
    if (!normalized) return '';
    if (normalized.includes('hybrid')) return 'hybrid';
    if (normalized.includes('memory foam') || normalized === 'foam' || normalized === 'memory foam') {
      return 'memory foam';
    }
    return normalized;
  }

  function normalizeHeight(value) {
    const normalized = normalizeText(firstValue(value));
    if (!normalized) return '';
    const match = normalized.match(/(\d+)/);
    return match ? `${match[1]}"` : normalized;
  }

  function normalizeCoolingLabel(value) {
    const numeric = Number.parseFloat(value);
    if (Number.isFinite(numeric)) {
      return numeric >= 7 ? 'extra' : 'standard';
    }

    const normalized = normalizeText(firstValue(value));
    if (!normalized) return '';
    if (normalized.includes('extra')) return 'extra';
    if (normalized.includes('standard')) return 'standard';
    return normalized;
  }

  function normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function formatMoney(cents, currency) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(Number(cents || 0) / 100);
  }

  function getVariantUrl(url, variantId) {
    const fallbackUrl = url || '#';
    if (!variantId || fallbackUrl === '#') return fallbackUrl;

    const [pathAndQuery, hash = ''] = fallbackUrl.split('#');
    const [path, query = ''] = pathAndQuery.split('?');
    const params = new URLSearchParams(query);
    params.set('variant', variantId);

    const queryString = params.toString();
    return `${path}${queryString ? `?${queryString}` : ''}${hash ? `#${hash}` : ''}`;
  }

  function renderSnowflakes(score) {
    const normalizedUnits = Math.max(0, Math.min(5, Math.round(Number(score || 0)) / 2));
    const fullCount = Math.floor(normalizedUnits);
    const hasHalf = normalizedUnits % 1 !== 0;
    let markup = '<div class="product-comparison-modal__scale" aria-label="Cooling score">';

    for (let index = 0; index < fullCount; index += 1) {
      markup += `<span class="product-comparison-modal__flake is-active">${getFullSnowflakeSvg()}</span>`;
    }

    if (hasHalf) {
      markup += `<span class="product-comparison-modal__flake is-half">${getHalfSnowflakeSvg(`half-${Math.random()
        .toString(36)
        .slice(2, 9)}`)}</span>`;
    }

    markup += '</div>';
    return markup;
  }

  function renderHeightValue(value) {
    return `
      <span class="product-comparison-modal__height-value">
        <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M22 11.8636V31.6364" stroke="#636466" stroke-width="1.5"/>
          <path d="M18 15.8182L22 11.8636L26 15.8182" stroke="#636466" stroke-width="1.5"/>
          <path d="M18 27.6818L22 31.6363L26 27.6818" stroke="#636466" stroke-width="1.5"/>
        </svg>
        <span>${escapeHtml(value)}</span>
      </span>
    `;
  }

  function renderSegments(score) {
    const normalizedUnits = Math.max(0, Math.min(5, Math.round(Number(score || 0)) / 2));
    const fullCount = Math.floor(normalizedUnits);
    const hasHalf = normalizedUnits % 1 !== 0;
    const emptyCount = Math.max(0, 5 - fullCount - (hasHalf ? 1 : 0));
    let markup = '<div class="product-comparison-modal__segments" aria-label="Score">';

    for (let index = 0; index < fullCount; index += 1) {
      markup += '<span class="product-comparison-modal__segment is-active"></span>';
    }

    if (hasHalf) {
      markup += '<span class="product-comparison-modal__segment is-half"></span>';
    }

    for (let index = 0; index < emptyCount; index += 1) {
      markup += '<span class="product-comparison-modal__segment"></span>';
    }

    markup += '</div>';
    return markup;
  }

  function getFullSnowflakeSvg() {
    return `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M19.8552 38.3334C19.4613 38.3334 19.1918 38.0017 19.1918 37.67V32.8193L16.5799 35.4727C16.4555 35.597 16.2897 35.68 16.1239 35.68C15.958 35.68 15.7922 35.6178 15.6678 35.4727C15.3983 35.2032 15.3983 34.83 15.6678 34.5606L19.2126 30.9121V22.7654L12.2474 26.8491L10.9207 31.7206V31.7413C10.8792 31.9901 10.5683 32.1766 10.2574 32.1766C10.2159 32.1766 10.1744 32.1766 10.133 32.1559C9.86349 32.0937 9.65619 31.7206 9.71838 31.3682L10.7341 27.7198L6.46382 30.2488C6.36017 30.2902 6.23579 30.311 6.11141 30.311C5.96631 30.311 5.69682 30.2695 5.51025 30C5.38588 29.6683 5.42734 29.2538 5.77974 29.0465L10.0501 26.6418L6.40163 25.6261H6.3809C6.11141 25.5639 5.88339 25.17 5.96631 24.8383C6.00777 24.5896 6.31871 24.403 6.62966 24.403H6.75404L11.6048 25.7297L18.6736 21.6667L11.5841 17.7073L6.69185 19.034H6.67112C6.62966 19.0547 6.5882 19.0547 6.52601 19.0547C6.27725 19.0547 5.98704 18.8267 5.94558 18.578V18.5365L5.92485 18.495C5.86266 18.3292 5.92485 18.1841 5.96631 18.1012C6.04923 17.9353 6.21506 17.811 6.36017 17.7902H6.3809L9.98787 16.7745L5.75901 14.2662C5.40661 14.0589 5.36515 13.6443 5.48952 13.3126C5.65536 13.0431 5.94558 13.0017 6.09069 13.0017C6.19433 13.0017 6.31871 13.0224 6.44309 13.0639L10.7134 15.5929L9.69765 11.9445C9.63546 11.5506 9.82203 11.1982 10.1537 11.0531H10.2574C10.5683 11.0531 10.8792 11.2397 10.9207 11.4884V11.5091L12.2474 16.3806L19.3162 20.4436V12.4212L15.7714 8.87647C15.6471 8.75209 15.5642 8.58626 15.5642 8.42042C15.5642 8.25458 15.6263 8.08875 15.7714 7.96437C15.8958 7.83999 16.0617 7.75707 16.2275 7.75707C16.3933 7.75707 16.5592 7.81926 16.6836 7.96437L19.3162 10.597V5.66337C19.3162 5.24878 19.565 5.02075 19.9588 5.02075C20.3527 5.02075 20.6222 5.35243 20.6222 5.66337V10.6592L23.2549 7.88145C23.3792 7.75707 23.5451 7.69488 23.7109 7.69488C23.8768 7.69488 24.0426 7.75707 24.167 7.90218C24.2913 8.02656 24.3743 8.19239 24.3743 8.35823C24.3743 8.52407 24.3121 8.68991 24.167 8.81428L20.6222 12.4627V20.6095L27.5874 16.5257L29.0177 11.5506V11.5299C29.0592 11.2811 29.3701 11.0945 29.6811 11.0945C29.7225 11.0945 29.764 11.0945 29.8054 11.1153C29.9713 11.1567 30.0957 11.3018 30.1371 11.3848C30.22 11.5506 30.2615 11.7372 30.22 11.903L29.3494 15.4892L33.4746 13.1053C33.5782 13.0639 33.7026 13.0431 33.827 13.0431C33.9721 13.0431 34.2416 13.0846 34.4282 13.3541C34.4904 13.5407 34.5111 13.7272 34.4696 13.8723C34.4282 14.0174 34.3452 14.1211 34.2209 14.1625L34.2001 14.1833L30.0127 16.6086L33.6404 17.6244H33.6612C33.9307 17.6866 34.1587 18.0805 34.0758 18.4121C34.0343 18.6609 33.7234 18.8475 33.4124 18.8475C33.3709 18.8475 33.3295 18.8475 33.288 18.8267L28.3336 17.6244L21.2648 21.6667L28.3336 25.6261L33.3295 24.403L33.3502 24.3823C33.3917 24.3615 33.4331 24.3615 33.4953 24.3615C33.7441 24.3615 34.0343 24.5896 34.0758 24.8383V24.8798L34.0965 24.9212C34.1587 25.0871 34.0965 25.2322 34.055 25.3151C33.9721 25.4809 33.8063 25.6053 33.6612 25.6261H33.6404L30.0335 26.6418L34.2001 29.0465C34.5111 29.233 34.6976 29.6062 34.4696 29.9793C34.3245 30.2073 34.0965 30.3524 33.8685 30.3524C33.7648 30.3524 33.6404 30.3317 33.5575 30.2695L29.3701 27.8234L30.4066 31.4926V31.5133C30.4688 31.6791 30.4066 31.8242 30.3651 31.9072C30.2822 32.073 30.1164 32.1974 29.9713 32.2181C29.9298 32.2181 29.8884 32.2388 29.8469 32.2388C29.536 32.2388 29.225 32.0523 29.1836 31.8035V31.762L27.7532 26.7869L20.6844 22.7239V30.8707L24.1048 34.5191C24.3743 34.7886 24.3743 35.1824 24.1048 35.4312C23.9804 35.5556 23.8146 35.6385 23.6487 35.6385C23.4829 35.6385 23.3171 35.5763 23.1927 35.4312L20.5185 32.84V37.6907C20.5185 38.0846 20.2698 38.3334 19.8552 38.3334Z" fill="#636466" stroke="#636466" stroke-width="0.5"/>
      </svg>
    `;
  }

  function getHalfSnowflakeSvg(idSuffix) {
    return `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <mask id="path-1-outside-1-${idSuffix}" maskUnits="userSpaceOnUse" x="4.42578" y="4.02075" width="31" height="35" fill="black">
          <rect fill="white" x="4.42578" y="4.02075" width="31" height="35"/>
          <path d="M19.8552 38.3334C19.4613 38.3334 19.1918 38.0017 19.1918 37.67V32.8193L16.5799 35.4727C16.4555 35.597 16.2897 35.68 16.1239 35.68C15.958 35.68 15.7922 35.6178 15.6678 35.4727C15.3983 35.2032 15.3983 34.83 15.6678 34.5606L19.2126 30.9121V22.7654L12.2474 26.8491L10.9207 31.7206V31.7413C10.8792 31.9901 10.5683 32.1766 10.2574 32.1766C10.2159 32.1766 10.1744 32.1766 10.133 32.1559C9.86349 32.0937 9.65619 31.7206 9.71838 31.3682L10.7341 27.7198L6.46382 30.2488C6.36017 30.2902 6.23579 30.311 6.11142 30.311C5.96631 30.311 5.69682 30.2695 5.51025 30C5.38588 29.6683 5.42734 29.2538 5.77974 29.0465L10.0501 26.6418L6.40163 25.6261H6.3809C6.11141 25.5639 5.88339 25.17 5.96631 24.8383C6.00777 24.5896 6.31871 24.403 6.62966 24.403H6.75404L11.6048 25.7297L18.6736 21.6667L11.5841 17.7073L6.69185 19.034H6.67112C6.62966 19.0547 6.5882 19.0547 6.52601 19.0547C6.27725 19.0547 5.98704 18.8267 5.94558 18.578V18.5365L5.92485 18.495C5.86266 18.3292 5.92485 18.1841 5.96631 18.1012C6.04923 17.9353 6.21506 17.811 6.36017 17.7902H6.3809L9.98787 16.7745L5.75901 14.2662C5.40661 14.0589 5.36515 13.6443 5.48952 13.3126C5.65536 13.0431 5.94558 13.0017 6.09069 13.0017C6.19433 13.0017 6.31871 13.0224 6.44309 13.0639L10.7134 15.5929L9.69765 11.9445C9.63546 11.5506 9.82203 11.1982 10.1537 11.0531H10.2574C10.5683 11.0531 10.8792 11.2397 10.9207 11.4884V11.5091L12.2474 16.3806L19.3162 20.4436V12.4213L15.7714 8.87647C15.6471 8.7521 15.5642 8.58626 15.5642 8.42042C15.5642 8.25458 15.6263 8.08875 15.7714 7.96437C15.8958 7.83999 16.0617 7.75707 16.2275 7.75707C16.3933 7.75707 16.5592 7.81926 16.6836 7.96437L19.3162 10.597V5.66337C19.3162 5.24878 19.565 5.02075 19.9588 5.02075C20.3527 5.02075 20.6222 5.35243 20.6222 5.66337V10.6592L23.2549 7.88145C23.3792 7.75707 23.5451 7.69488 23.7109 7.69488C23.8768 7.69488 24.0426 7.75707 24.167 7.90218C24.2913 8.02656 24.3743 8.19239 24.3743 8.35823C24.3743 8.52407 24.3121 8.68991 24.167 8.81428L20.6222 12.4627V20.6095L27.5874 16.5257L29.0177 11.5506V11.5299C29.0592 11.2811 29.3701 11.0946 29.6811 11.0946C29.7225 11.0946 29.764 11.0946 29.8054 11.1153C29.9713 11.1567 30.0957 11.3018 30.1371 11.3848C30.22 11.5506 30.2615 11.7372 30.22 11.903L29.3494 15.4892L33.4746 13.1053C33.5782 13.0639 33.7026 13.0431 33.827 13.0431C33.9721 13.0431 34.2416 13.0846 34.4282 13.3541C34.4904 13.5407 34.5111 13.7272 34.4696 13.8723C34.4282 14.0174 34.3452 14.1211 34.2209 14.1625L34.2001 14.1833L30.0127 16.6086L33.6404 17.6244H33.6612C33.9307 17.6866 34.1587 18.0805 34.0758 18.4121C34.0343 18.6609 33.7234 18.8475 33.4124 18.8475C33.371 18.8475 33.3295 18.8475 33.288 18.8267L28.3336 17.6244L21.2648 21.6667L28.3336 25.6261L33.3295 24.403L33.3502 24.3823C33.3917 24.3615 33.4331 24.3615 33.4953 24.3615C33.7441 24.3615 34.0343 24.5896 34.0758 24.8383V24.8798L34.0965 24.9212C34.1587 25.0871 34.0965 25.2322 34.055 25.3151C33.9721 25.4809 33.8063 25.6053 33.6612 25.6261H33.6404L30.0335 26.6418L34.2001 29.0465C34.5111 29.233 34.6976 29.6062 34.4696 29.9793C34.3245 30.2073 34.0965 30.3524 33.8685 30.3524C33.7648 30.3524 33.6404 30.3317 33.5575 30.2695L29.3701 27.8234L30.4066 31.4926V31.5133C30.4688 31.6791 30.4066 31.8242 30.3651 31.9072C30.2822 32.073 30.1164 32.1974 29.9713 32.2181C29.9298 32.2181 29.8884 32.2388 29.8469 32.2388C29.536 32.2388 29.225 32.0523 29.1836 31.8035V31.762L27.7532 26.7869L20.6844 22.7239V30.8707L24.1048 34.5191C24.3743 34.7886 24.3743 35.1824 24.1048 35.4312C23.9804 35.5556 23.8146 35.6385 23.6487 35.6385C23.4829 35.6385 23.3171 35.5763 23.1927 35.4312L20.5185 32.84V37.6907C20.5185 38.0846 20.2698 38.3334 19.8552 38.3334Z"/>
        </mask>
        <path d="M19.8552 38.3334C19.4613 38.3334 19.1918 38.0017 19.1918 37.67V32.8193L16.5799 35.4727C16.4555 35.597 16.2897 35.68 16.1239 35.68C15.958 35.68 15.7922 35.6178 15.6678 35.4727C15.3983 35.2032 15.3983 34.83 15.6678 34.5606L19.2126 30.9121V22.7654L12.2474 26.8491L10.9207 31.7206V31.7413C10.8792 31.9901 10.5683 32.1766 10.2574 32.1766C10.2159 32.1766 10.1744 32.1766 10.133 32.1559C9.86349 32.0937 9.65619 31.7206 9.71838 31.3682L10.7341 27.7198L6.46382 30.2488C6.36017 30.2902 6.23579 30.311 6.11142 30.311C5.96631 30.311 5.69682 30.2695 5.51025 30C5.38588 29.6683 5.42734 29.2538 5.77974 29.0465L10.0501 26.6418L6.40163 25.6261H6.3809C6.11141 25.5639 5.88339 25.17 5.96631 24.8383C6.00777 24.5896 6.31871 24.403 6.62966 24.403H6.75404L11.6048 25.7297L18.6736 21.6667L11.5841 17.7073L6.69185 19.034H6.67112C6.62966 19.0547 6.5882 19.0547 6.52601 19.0547C6.27725 19.0547 5.98704 18.8267 5.94558 18.578V18.5365L5.92485 18.495C5.86266 18.3292 5.92485 18.1841 5.96631 18.1012C6.04923 17.9353 6.21506 17.811 6.36017 17.7902H6.3809L9.98787 16.7745L5.75901 14.2662C5.40661 14.0589 5.36515 13.6443 5.48952 13.3126C5.65536 13.0431 5.94558 13.0017 6.09069 13.0017C6.19433 13.0017 6.31871 13.0224 6.44309 13.0639L10.7134 15.5929L9.69765 11.9445C9.63546 11.5506 9.82203 11.1982 10.1537 11.0531H10.2574C10.5683 11.0531 10.8792 11.2397 10.9207 11.4884V11.5091L12.2474 16.3806L19.3162 20.4436V12.4213L15.7714 8.87647C15.6471 8.7521 15.5642 8.58626 15.5642 8.42042C15.5642 8.25458 15.6263 8.08875 15.7714 7.96437C15.8958 7.83999 16.0617 7.75707 16.2275 7.75707C16.3933 7.75707 16.5592 7.81926 16.6836 7.96437L19.3162 10.597V5.66337C19.3162 5.24878 19.565 5.02075 19.9588 5.02075C20.3527 5.02075 20.6222 5.35243 20.6222 5.66337V10.6592L23.2549 7.88145C23.3792 7.75707 23.5451 7.69488 23.7109 7.69488C23.8768 7.69488 24.0426 7.75707 24.167 7.90218C24.2913 8.02656 24.3743 8.19239 24.3743 8.35823C24.3743 8.52407 24.3121 8.68991 24.167 8.81428L20.6222 12.4627V20.6095L27.5874 16.5257L29.0177 11.5506V11.5299C29.0592 11.2811 29.3701 11.0946 29.6811 11.0946C29.7225 11.0946 29.764 11.0946 29.8054 11.1153C29.9713 11.1567 30.0957 11.3018 30.1371 11.3848C30.22 11.5506 30.2615 11.7372 30.22 11.903L29.3494 15.4892L33.4746 13.1053C33.5782 13.0639 33.7026 13.0431 33.827 13.0431C33.9721 13.0431 34.2416 13.0846 34.4282 13.3541C34.4904 13.5407 34.5111 13.7272 34.4696 13.8723C34.4282 14.0174 34.3452 14.1211 34.2209 14.1625L34.2001 14.1833L30.0127 16.6086L33.6404 17.6244H33.6612C33.9307 17.6866 34.1587 18.0805 34.0758 18.4121C34.0343 18.6609 33.7234 18.8475 33.4124 18.8475C33.371 18.8475 33.3295 18.8475 33.288 18.8267L28.3336 17.6244L21.2648 21.6667L28.3336 25.6261L33.3295 24.403L33.3502 24.3823C33.3917 24.3615 33.4331 24.3615 33.4953 24.3615C33.7441 24.3615 34.0343 24.5896 34.0758 24.8383V24.8798L34.0965 24.9212C34.1587 25.0871 34.0965 25.2322 34.055 25.3151C33.9721 25.4809 33.8063 25.6053 33.6612 25.6261H33.6404L30.0335 26.6418L34.2001 29.0465C34.5111 29.233 34.6976 29.6062 34.4696 29.9793C34.3245 30.2073 34.0965 30.3524 33.8685 30.3524C33.7648 30.3524 33.6404 30.3317 33.5575 30.2695L29.3701 27.8234L30.4066 31.4926V31.5133C30.4688 31.6791 30.4066 31.8242 30.3651 31.9072C30.2822 32.073 30.1164 32.1974 29.9713 32.2181C29.9298 32.2181 29.8884 32.2388 29.8469 32.2388C29.536 32.2388 29.225 32.0523 29.1836 31.8035V31.762L27.7532 26.7869L20.6844 22.7239V30.8707L24.1048 34.5191C24.3743 34.7886 24.3743 35.1824 24.1048 35.4312C23.9804 35.5556 23.8146 35.6385 23.6487 35.6385C23.4829 35.6385 23.3171 35.5763 23.1927 35.4312L20.5185 32.84V37.6907C20.5185 38.0846 20.2698 38.3334 19.8552 38.3334Z" stroke="#DEDFE0" stroke-width="0.5" mask="url(#path-1-outside-1-${idSuffix})"/>
        <mask id="mask0_${idSuffix}" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="5" y="4" width="30" height="35">
          <path d="M19.8552 38.3334C19.4613 38.3334 19.1918 38.0017 19.1918 37.67V32.8193L16.5799 35.4727C16.4555 35.597 16.2897 35.68 16.1239 35.68C15.958 35.68 15.7922 35.6178 15.6678 35.4727C15.3983 35.2032 15.3983 34.83 15.6678 34.5606L19.2126 30.9121V22.7654L12.2474 26.8491L10.9207 31.7206V31.7413C10.8792 31.9901 10.5683 32.1766 10.2574 32.1766C10.2159 32.1766 10.1744 32.1766 10.133 32.1559C9.86349 32.0937 9.65619 31.7206 9.71838 31.3682L10.7341 27.7198L6.46382 30.2488C6.36017 30.2902 6.23579 30.311 6.11142 30.311C5.96631 30.311 5.69682 30.2695 5.51025 30C5.38588 29.6683 5.42734 29.2538 5.77974 29.0465L10.0501 26.6418L6.40163 25.6261H6.3809C6.11141 25.5639 5.88339 25.17 5.96631 24.8383C6.00777 24.5896 6.31871 24.403 6.62966 24.403H6.75404L11.6048 25.7297L18.6736 21.6667L11.5841 17.7073L6.69185 19.034H6.67112C6.62966 19.0547 6.5882 19.0547 6.52601 19.0547C6.27725 19.0547 5.98704 18.8267 5.94558 18.578V18.5365L5.92485 18.495C5.86266 18.3292 5.92485 18.1841 5.96631 18.1012C6.04923 17.9353 6.21506 17.811 6.36017 17.7902H6.3809L9.98787 16.7745L5.75901 14.2662C5.40661 14.0589 5.36515 13.6443 5.48952 13.3126C5.65536 13.0431 5.94558 13.0017 6.09069 13.0017C6.19433 13.0017 6.31871 13.0224 6.44309 13.0639L10.7134 15.5929L9.69765 11.9445C9.63546 11.5506 9.82203 11.1982 10.1537 11.0531H10.2574C10.5683 11.0531 10.8792 11.2397 10.9207 11.4884V11.5091L12.2474 16.3806L19.3162 20.4436V12.4213L15.7714 8.87647C15.6471 8.7521 15.5642 8.58626 15.5642 8.42042C15.5642 8.25458 15.6263 8.08875 15.7714 7.96437C15.8958 7.83999 16.0617 7.75707 16.2275 7.75707C16.3933 7.75707 16.5592 7.81926 16.6836 7.96437L19.3162 10.597V5.66337C19.3162 5.24878 19.565 5.02075 19.9588 5.02075C20.3527 5.02075 20.6222 5.35243 20.6222 5.66337V10.6592L23.2549 7.88145C23.3792 7.75707 23.5451 7.69488 23.7109 7.69488C23.8768 7.69488 24.0426 7.75707 24.167 7.90218C24.2913 8.02656 24.3743 8.19239 24.3743 8.35823C24.3743 8.52407 24.3121 8.68991 24.167 8.81428L20.6222 12.4627V20.6095L27.5874 16.5257L29.0177 11.5506V11.5299C29.0592 11.2811 29.3701 11.0946 29.6811 11.0946C29.7225 11.0946 29.764 11.0946 29.8054 11.1153C29.9713 11.1567 30.0957 11.3018 30.1371 11.3848C30.22 11.5506 30.2615 11.7372 30.22 11.903L29.3494 15.4892L33.4746 13.1053C33.5782 13.0639 33.7026 13.0431 33.827 13.0431C33.9721 13.0431 34.2416 13.0846 34.4282 13.3541C34.4904 13.5407 34.5111 13.7272 34.4696 13.8723C34.4282 14.0174 34.3452 14.1211 34.2209 14.1625L34.2001 14.1833L30.0127 16.6086L33.6404 17.6244H33.6612C33.9307 17.6866 34.1587 18.0805 34.0758 18.4121C34.0343 18.6609 33.7234 18.8475 33.4124 18.8475C33.371 18.8475 33.3295 18.8475 33.288 18.8267L28.3336 17.6244L21.2648 21.6667L28.3336 25.6261L33.3295 24.403L33.3502 24.3823C33.3917 24.3615 33.4331 24.3615 33.4953 24.3615C33.7441 24.3615 34.0343 24.5896 34.0758 24.8383V24.8798L34.0965 24.9212C34.1587 25.0871 34.0965 25.2322 34.055 25.3151C33.9721 25.4809 33.8063 25.6053 33.6612 25.6261H33.6404L30.0335 26.6418L34.2001 29.0465C34.5111 29.233 34.6976 29.6062 34.4696 29.9793C34.3245 30.2073 34.0965 30.3524 33.8685 30.3524C33.7648 30.3524 33.6404 30.3317 33.5575 30.2695L29.3701 27.8234L30.4066 31.4926V31.5133C30.4688 31.6791 30.4066 31.8242 30.3651 31.9072C30.2822 32.073 30.1164 32.1974 29.9713 32.2181C29.9298 32.2181 29.8884 32.2388 29.8469 32.2388C29.536 32.2388 29.225 32.0523 29.1836 31.8035V31.762L27.7532 26.7869L20.6844 22.7239V30.8707L24.1048 34.5191C24.3743 34.7886 24.3743 35.1824 24.1048 35.4312C23.9804 35.5556 23.8146 35.6385 23.6487 35.6385C23.4829 35.6385 23.3171 35.5763 23.1927 35.4312L20.5185 32.84V37.6907C20.5185 38.0846 20.2698 38.3334 19.8552 38.3334Z" fill="#636466" stroke="#B9BABC" stroke-width="0.5"/>
        </mask>
        <g mask="url(#mask0_${idSuffix})">
          <rect x="1.33203" y="2.66675" width="18.6667" height="37.3333" fill="#636466"/>
        </g>
      </svg>
    `;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
})();
